import { Dictionary } from '@teinor/erp/types/globals'
import { PdfModalEditorProps } from './types'
import TableEditor from './CPTTableEditor'
import { faChevronDown, faChevronUp } from '@fortawesome/pro-light-svg-icons'
import { useState } from 'react'
import { ComponentWithPermissions } from '../../../../../../../../../../../baseComponents/CreateRoutering/types'
import NullComponent from '../../../../../../../../../../../baseComponents/NullComponent'
import TwinIcon from '../../../../../../../../../../../baseComponents/TwinIcon'
import FormRenderer from '../../../../../../../../../../../forms/FormRenderer'
import { FormElement, FormRender } from '../../../../../../../../../../../forms/FormRenderer/types'
import { valueOrDefaultValue } from '../../../../../../../../../../../utils/globals/valueOrDefaultValue'
import useTwinTranslation from '../../../../../../../../../../../utils/hooks/useTwinTranslation'
import { PdfConfigTemplateAllModulesKey, PdfCssConfig } from '../../../../../types'
import TableRecursiveEditor from './CPTTableRecursiveEditor'
import useInputColorColors from '../../../../../../../../../../../utils/hooks/pdf/useInputColorColors'
import useTicketVariables from '../../../../../../../../../../../utils/hooks/pdf/useTicketVariables'
import { returnClassOfSpanTemplate } from '../../../../../../../../../../../utils/globals/tailwind'
import { addDictionaryExtraLine } from '../../../../../../../../../../../utils/globals/dictionary'
import { emptyExtraLineFirstSortFunction } from '../../../../../../../../../../../forms/CustomSelect/functions'


type TextEditorProps =  PdfModalEditorProps & ComponentWithPermissions & { }

const TextEditor: React.FC<TextEditorProps> = ({ userPermissions, pdfConfigElement }) => {
    const {t} = useTwinTranslation()
    const sections: FormRender<Dictionary> = [
        {
            cols: 1,
            className: 'mt-20',
            title: t('config', 'Configuración'),
            elements: [
                {
                    name: 'text',
                    label: t('text', 'Texto'),
                    component: 'TextArea',
                },
            ]
        }
    ]
    const allRowData = { ...pdfConfigElement.extraProps }
    const parsedFields = valueOrDefaultValue(sections, userPermissions, allRowData)
    return (
        <div className='cpt_text_editor mt-17'>
            <FormRenderer sections={parsedFields} />    
        </div>
    )
}
type QrEditorProps =  PdfModalEditorProps & ComponentWithPermissions & { }

const QrEditor: React.FC<QrEditorProps> = ({ userPermissions, pdfConfigElement }) => {
    const { t } = useTwinTranslation()
    const { cellSizes, model, correction } = useTicketVariables()
    const configurationSections: FormRender<Dictionary> = [
        {
            cols: 1,
            className: 'mt-20',
            title: t('config', 'Configuración'),
            elements: [
                {
                    name: 'text',
                    label: t('text', 'Texto'),
                    component: 'TextArea',
                },
            ]
        },
        {
            cols: 3,
            elements: [
                {
                    name: 'cellSize',
                    label: t('cellSize', 'Tamaño de celda'),
                    component: 'CustomSelect',
                    items: cellSizes
                },
                {
                    name: 'model',
                    label: t('model', 'Modelo'),
                    component: 'CustomSelect',
                    items: model
                },
                {
                    name: 'correction',
                    label: t('correction', 'Corrección'),
                    component: 'CustomSelect',
                    items: correction
                },
            ]
        }
    ]
    const allRowData = { ...pdfConfigElement.extraProps }
    const configurationFields = valueOrDefaultValue(configurationSections, userPermissions, allRowData)
    return (
        <div className='cpt_text_editor mt-17 flex'>
            <FormRenderer sections={configurationFields}/>    
        </div>
    )
}
type BarcodeEditorProps =  PdfModalEditorProps & ComponentWithPermissions & { }

const BarcodeEditor: React.FC<BarcodeEditorProps> = ({ userPermissions, pdfConfigElement, documentType }) => {
    const { t } = useTwinTranslation()
    const { width, textTy } = useTicketVariables()
    const voucherElements = []
    if (documentType === 'voucher' || documentType === 'ticket' || documentType === 'ticketRefund') {
        voucherElements.push(
            {
                name: 'widthTy',
                label: t('width', 'Ancho'),
                component: 'CustomSelect',
                items: addDictionaryExtraLine(width),
                sortFunction: emptyExtraLineFirstSortFunction
            },
            {
                name: 'heightTy',
                id: 'height',
                label: t('height', 'Altura'),
                component: 'InputWithLabelMargin',
                type: 'text',
            },
        )
    }
    const configurationSections: FormRender<Dictionary> = [
        {
            cols: 1,
            className: 'mt-20',
            title: t('config', 'Configuración'),
            elements: [
                {
                    name: 'text',
                    label: t('text', 'Texto'),
                    component: 'TextArea',
                    className: returnClassOfSpanTemplate(2),
                },
            ]
        },
        {
            cols: 3,
            elements: [
                {
                    name: 'textTy',
                    label: t('barcodeText', 'Texto en el código de barras'),
                    component: 'CustomSelect',
                    items: textTy
                },
                ...voucherElements as any
            ]
        }
    ]
  
    const allRowData = { ...pdfConfigElement.extraProps }
    const configurationFields = valueOrDefaultValue(configurationSections, userPermissions, allRowData)
    return (
        <div className='cpt_text_editor mt-17 flex'>
            <FormRenderer sections={configurationFields}/>    
        </div>
    )
}
type LogoEditorProps =  PdfModalEditorProps & ComponentWithPermissions & { }

const LogoEditor: React.FC<LogoEditorProps> = ({ userPermissions, pdfConfigElement }) => {
    const { t } = useTwinTranslation()
    const configurationSections: FormRender<Dictionary> = [
        {
            cols: 3,
            className: 'mt-20',
            title: t('config', 'Configuración'),
            elements: [
                {
                    name: 'maxWidthLogo',
                    label: t('maxWidthLogo', 'Ancho máximo del logo'),
                    component: 'InputWithLabelMargin',
                    type: 'text',
                },
            ]
        }
    ]
    const allRowData = { ...pdfConfigElement.extraProps }
    const configurationFields = valueOrDefaultValue(configurationSections, userPermissions, allRowData)
    return (
        <div className='cpt_text_editor mt-17 flex'>
            <FormRenderer sections={configurationFields}/>    
        </div>
    )
}


const Modules: Record<PdfConfigTemplateAllModulesKey, React.FC<PdfModalEditorProps & ComponentWithPermissions>> = {
    Logo: LogoEditor,
    Text: TextEditor,
    Table: TableEditor,
    TableRecursive: TableRecursiveEditor,
    DrawLine: NullComponent,
    PrintBarcode: BarcodeEditor,
    PrintQR: QrEditor
}

type CPTCEModalEditorProps = PdfModalEditorProps & ComponentWithPermissions & {
}

const CPTCEModalEditor: React.FC<CPTCEModalEditorProps> = ({ pdfConfigElement, userPermissions, documentType, ...rest }) => {
    const { t } = useTwinTranslation()
    const [opened, setOpened] = useState(false)
    const { colors } = useInputColorColors()
    const { fontSizes, fontWeights } = useTicketVariables()
    const allRowData = { ...pdfConfigElement.cssConfig }
    let elements: FormElement<keyof PdfCssConfig>[] = []
    if (documentType === 'ticket' || documentType === 'ticketRefund' || documentType === 'voucher' || documentType === 'giftTicket' || documentType === 'cashRegister') {
        elements = [
            {
                name: 'fontSizeTicket',
                label: t('fontSize', 'Tamaño de letra'),
                component: 'CustomSelect',
                items: fontSizes
            },
            {
                name: 'fontWeight',
                label: t('fontWeight', 'Grosor letra'),
                component: 'CustomSelect',
                items: fontWeights
            },
        ]
    } else {
        elements = [
            {
                name: 'fontSize',
                label: t('fontSize', 'Tamaño de letra'),
                id: 'fontSize',
                component: 'InputWithLabelMargin',
                type: 'text'
            },
            {
                name: 'color',
                label: t('letterColor', 'Color de letra'),
                id: 'color',
                component: 'InputColorDeleteButtonWithMargin',
                colors: colors,
            },
            {
                name: 'background',
                id: 'background',
                label: t('backgroundColor', 'Color de fondo'),
                component: 'InputColorDeleteButtonWithMargin',
                colors: colors,
            },
            {
                name: 'padding',
                id: 'padding',
                label: t('padding', 'Margen interior'),
                component: 'InputWithLabelMargin',
                type: 'text'
            },
            {
                name: 'margin',
                id: 'margin',
                label: t('marginExt', 'Margen exterior'),
                component: 'InputWithLabelMargin',
                type: 'text'
            },
            {
                name: 'textAlign',
                label: t('textAlign', 'Alineación texto'),
                component: 'CustomSelect',
                items: {
                    'left': { name: t('left', 'Izquierda') },
                    'center': { name: t('center', 'Centro') },
                    'right': { name: t('right', 'Derecha') }
                },
                value: allRowData.textAlign || 'left'
            },
            {
                name: 'border',
                label: t('border', 'Borde'),
                component: 'InputWithLabelMargin',
                type: 'text'
            },
            {
                name: 'borderBottom',
                label: t('borderBottom', 'Borde inferior'),
                component: 'InputWithLabelMargin',
                type: 'text'
            },
            {
                name: 'height',
                id: 'height',
                label: t('height', 'Altura'),
                component: 'InputWithLabelMargin',
                type: 'text'
            },
            {
                name: 'width',
                id: 'width',
                label: t('width', 'Ancho'),
                component: 'InputWithLabelMargin',
                type: 'text'
            },
            {
                name: 'fontWeight',
                id: 'fontWeight',
                label: t('fontWeight', 'Grosor letra'),
                component: 'InputWithLabelMargin',
                type: 'text'
            },
        ]
    }
    const sections: FormRender<PdfCssConfig> = [
        {
            cols: 3,
            className: 'mt-20 ' + (opened ? '' : 'hidden'),
            title: <div className='cursor-pointer' onClick={() => setOpened((old) => !old)}><span>{t('styles', 'Estilos')}</span><TwinIcon icon={opened ? faChevronUp : faChevronDown} className='ml-10'/></div>,
            elements: elements
        }
    ]
    const ModuleComponent = Modules[pdfConfigElement.component]
    const parsedFields = valueOrDefaultValue(sections, userPermissions, allRowData)

    return (
        <div>
            <FormRenderer sections={parsedFields || []} />
            <ModuleComponent pdfConfigElement={pdfConfigElement} userPermissions={userPermissions} {...rest} documentType={documentType}/>
        </div>
    )
}



export default CPTCEModalEditor